var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { CustomComponent } from '../../utils/CustomComponent';
import { defaultAnimationDuration } from '../../utils/animations';
import { getBoxColorProps } from '../../utils/boxStyle';
import { omitStyleProps } from '../../utils/props';
import { applyFlexbox, applyFlexboxInlineStyle, applyPositionInlineStyle, applySizeInlineStyle, applySpacings, } from '../../utils/tailwind';
import { applyGrid, applyGridInlineStyle } from '../../utils/tailwind/grid';
import { useColorSet } from '../ThemeProvider/useColorSet';
import { useTheme } from '../ThemeProvider/useTheme';
import { box } from './styles';
const BoxComponent = (_a, ref) => {
    var { children, className, component = 'div', colorSet, animation, isDisplayed = true, animateInitialRender, borderWidth, borderRadius, withBorder, backgroundColor, style, onClick } = _a, props = __rest(_a, ["children", "className", "component", "colorSet", "animation", "isDisplayed", "animateInitialRender", "borderWidth", "borderRadius", "withBorder", "backgroundColor", "style", "onClick"]);
    const duration = (animation === null || animation === void 0 ? void 0 : animation.duration) || defaultAnimationDuration;
    const colors = useColorSet({ colorSet });
    const theme = useTheme();
    const boxColors = useMemo(() => {
        return getBoxColorProps({
            colorSet,
            colors,
            theme,
            backgroundColor,
            shadowElevation: props.shadowElevation,
            borderColor: props.borderColor,
        });
    }, [colorSet, colors, backgroundColor, props.borderColor, props.shadowElevation, theme]);
    const [animationName, setAnimationName] = useState(null);
    const [isFirstRender, setIsFirstRender] = useState(true);
    useEffect(() => {
        setIsFirstRender(false);
    }, []);
    useEffect(() => {
        // if we don't want to animate the box when it is mounted directly visible.
        if (isFirstRender && !animateInitialRender) {
            return;
        }
        if (isDisplayed && (animation === null || animation === void 0 ? void 0 : animation.in)) {
            setAnimationName(animation.in);
        }
        else if (!isDisplayed && (animation === null || animation === void 0 ? void 0 : animation.out)) {
            setAnimationName(animation.out);
        }
    }, [isDisplayed]);
    // we don't want to react on `firstRender` or `animateInitialRender` changes.
    // we just want to react on `isDisplayed` to know whether we should trigger in/out animation.
    /**
     * Do not display the box depending on the `isDisplayed` prop.
     * The `!animationName` is needed to keep the box displayed during
     * the "out" animation.
     */
    if (!(isDisplayed || animationName)) {
        return null;
    }
    /*
      Avoid a flash where the box will appear at its final
      position for a frame before animating its entering.
      This is happening because the `animationName` is not
      yet set at first render (it needs a first `useEffect` to be
      run).
      We do this check only if there is an animation, hence
      the `!!animation`.
     */
    if (!!animation && isFirstRender && !animationName) {
        return null;
    }
    return (_jsx(CustomComponent, Object.assign({ ref: ref, as: component }, omitStyleProps(props), { onAnimationEnd: animation ? () => setAnimationName(null) : undefined, onClick: onClick, className: box(Object.assign(Object.assign({ border: withBorder, display: props.display, position: props.position, shadowElevation: props.shadowElevation, overflow: props.overflow }, (animationName && { animation: animationName })), { className: [applySpacings(props), applyFlexbox(props), applyGrid(props), className] })), style: Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, applyGridInlineStyle(props)), applyFlexboxInlineStyle(props)), applyPositionInlineStyle(props)), applySizeInlineStyle(props)), { 
            // Border dynamic props
            borderRadius }), (withBorder && Object.assign(Object.assign({}, (borderWidth && { borderWidth: `${borderWidth}px` })), (!boxColors.backgroundColor && {
            borderColor: boxColors.borderColor,
        })))), (boxColors.backgroundColor && {
            background: `${boxColors.backgroundColor} padding-box, ${boxColors.borderColor} border-box`,
        })), (duration !== defaultAnimationDuration && {
            animationDuration: `${duration}ms`,
        })), style), children: children })));
};
/**
 * @deprecated Use a native HTML element like `div` and style it using the `className` prop.
 */
export const Box = forwardRef(BoxComponent);
